<script setup>
import dayjs from 'dayjs'
import {computed} from "vue";
import {useModalStore} from "../../store/modal";

const modalStore = useModalStore();

const props = defineProps({
    ticket: {
        type: Object,
        required: true
    },
    completed: {
        type: Boolean,
        default: false
    },
    selected: {
        type: Boolean,
        default: false
    },
    showCheckbox: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['select', 'deselect'])

const assignmentSlot = computed(() => props.completed
    ? 'completed'
    : (props.ticket.assignee ? 'assignee' : 'assign')
)

const icon = computed(() => {
    switch (props.ticket.status) {
        case 'created':
            return 'exclaim-default'
        case 'complete':
            return 'check-all'
        default:
            return 'speechbubble-double'
    }
})

const assign = () => {
    modalStore.open(
        "AssignTicket",
        {
            id: props.ticket.id,
            assigned: !!props.ticket.assignee_id
        },
        'body',
        'bottom-right'
    )
}

const showActions = () => {
    modalStore.open(
        "Ticket",
        {
            id: props.ticket.id,
            assigned: !!props.ticket.assignee_id,
            status: props.ticket.status,
            external_url: props.ticket.external_url
        },
        `#ticket-row-${props.ticket.id}`,
        'bottom-center'
    )
}

const statusLabel = computed(() => {
    switch (props.ticket.status) {
        case 'created':
            return 'Created'
        case 'assigned':
            return 'Assigned'
        case 'awaiting-external':
            return 'Awaiting external'
        case 'complete':
            return 'Complete'
        default:
            return props.ticket.status
    }
})

const contentPreview = computed(() => {
    const content = props.ticket.content || ''

    return content.length > 80
        ? `${content.substring(0, 80)}...`
        : content
})

const handleCheckboxChange = (event) => {
    event.preventDefault()
    event.stopPropagation()

    if (props.selected) {
        emit('deselect', props.ticket.id)
    } else {
        emit('select', props.ticket.id)
    }
}

const clientReseller = computed(() => {
    if (!props.ticket.reseller_order) {
        return null
    }

    return props.ticket.reseller_order.reseller?.name
        || props.ticket.reseller_order.reseller?.company_name
        || 'Linked'
})
</script>

<template>
    <router-link
        :to="`/tickets/view/${ticket.id}`"
        class="ticket-row-item"
        :class="{'--with-checkbox': showCheckbox}"
        :id="`ticket-row-${ticket.id}`"
    >
        <div
            v-if="showCheckbox"
            class="ticket-checkbox"
            @click.stop="handleCheckboxChange"
        >
            <input
                type="checkbox"
                :checked="selected"
                @change="handleCheckboxChange"
                @click.stop
            />
        </div>

        <ah-icon
            class="ticket-status-icon"
            :name="icon"
        />

        <dl class="ticket-timestamp">
            <dt>{{ dayjs(ticket.created_at).format('DD MMM YYYY') }}</dt>
            <dd>{{ dayjs(ticket.created_at).format('HH:mm') }}</dd>
        </dl>

        <dl class="ticket-meta ticket-meta-kayako">
            <dt>Kayako number.</dt>
            <dd>{{ ticket.external_id }}</dd>
        </dl>

        <dl class="ticket-meta ticket-meta-content">
            <dt>Content.</dt>
            <dd class="truncated-text">{{ contentPreview }}</dd>
        </dl>

        <dl class="ticket-meta ticket-meta-client">
            <dt>Client / reseller.</dt>
            <dd>
                {{ clientReseller || 'Unlinked' }}
            </dd>
        </dl>

        <dl class="ticket-meta ticket-meta-status">
            <dt>Status.</dt>
            <dd>
                <span class="ticket-status">
                    {{ statusLabel }}
                </span>
            </dd>
        </dl>

        <dl
            v-if="assignmentSlot === 'completed'"
            class="ticket-meta ticket-meta-assignee"
        >
            <dt>Completed.</dt>
            <dd>{{ dayjs(ticket.completed_at).format('DD MMM YYYY, HH:mm') }}</dd>
        </dl>

        <dl
            v-else-if="assignmentSlot === 'assignee'"
            class="ticket-meta ticket-meta-assignee"
        >
            <dt>Assigned to.</dt>
            <dd>
                {{ ticket.assignee.first_name }}
                {{ ticket.assignee.last_name?.[0] }}
            </dd>
        </dl>

        <button
            v-else
            @click.prevent="assign"
            class="ticket-assign"
        >
            <span class="label">Assign</span>
            <ah-icon name="user-single-add" />
        </button>

        <div
            @click.prevent="showActions"
            class="ticket-action"
        >
            <ah-icon name="more-default" />
        </div>

        <div class="ticket-navigation">
            <ah-icon name="arrow-right" />
        </div>
    </router-link>
</template>

<style lang="scss">
.truncated-text {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
.ticket-row-item {
    display: grid;
    padding: 0 2rem;
    grid-template-columns: 2.4rem 12rem repeat(4, 1fr) 18rem 3.6rem 3.6rem;
    align-content: center;
    gap: 2rem;

    &.--with-checkbox {
        grid-template-columns: 2.4rem 2.4rem 12rem repeat(4, 1fr) 18rem 3.6rem 3.6rem;
    }

    background: #fff;
    border-radius: 2.4rem;
    height: 8rem;
    color: var(--color-grey-thunder);
    cursor: pointer;
    transition: all .2s ease;
    position: relative;

    > * {
        align-self: center;
        cursor: pointer;
    }

    .ticket-checkbox {
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;

        input[type="checkbox"] {
            width: 2rem;
            height: 2rem;
            cursor: pointer;
        }
    }

    .ticket-status-icon {
        fill: var(--color-theme-primary);
        transition: fill .2s ease;
    }

    .ticket-timestamp {
        background: var(--color-grey-mist);
        height: 6rem;
        border-radius: 1.8rem;
        border: .1rem solid var(--color-grey-dew);
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        transition: all .2s ease;

        dt, dd {
            @include font(1.2, null, black);
        }

        dt {
            margin-bottom: .6rem;
        }
    }

    .ticket-meta {
        dt {
            @include font(1.4, null, black);
            margin-bottom: .6rem;
        }

        dd {
            @include font(1.2, null, book);
        }
    }

    .ticket-status {
        text-transform: capitalize;
        //background: var(--color-grey-mist);
        border: .1rem solid var(--color-grey-dew);
        text-align: center;
        @include font(1.1, 3, black);
        border-radius: 1.5rem;
        transition: all .2s ease;
    }

    .ticket-assign {
        border: none;
        background: var(--color-theme-primary);
        height: 4.4rem;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 1rem;
        padding: 0 3rem;
        border-radius: 2.2rem;
        transition: background-color .2s ease;

        .label {
            color: #fff;
            font-size: 1.4rem;
            font-weight: var(--font-weight-black);
        }

        .icon {
            fill: #fff;
        }
    }

    .ticket-action,
    .ticket-navigation {
        width: 3.6rem;
        height: 3.6rem;
        border-radius: 50%;
        background: transparent;
        fill: var(--color-grey-hazy);
        display: flex;
        justify-content: center;
        align-items: center;
        transition: all .2s ease;
    }

    @include hover() {
        background: var(--color-theme-primary);
        color: #fff;

        .ticket-status-icon {
            fill: #fff;
        }

        .ticket-timestamp {
            background: var(--color-black-50);
            border-color: transparent;
        }

        .ticket-status {
            border-color: transparent;
        }

        .ticket-assign {
            background: var(--color-black-50);
        }

        .ticket-action,
        .ticket-navigation {
            background: #fff;

            @include hover() {
                background: var(--color-red-raspberry);
                fill: #fff;
            }
        }
    }

    @include dark() {
        background-color: var(--color-darth-maul);
        color: #fff;

        .ticket-timestamp {
            background: var(--color-black-50);
            border-color: transparent;
        }

        .ticket-status {
            border-color: transparent;
        }

        .ticket-action,
        .ticket-navigation {
            fill: var(--color-grey-dew);
        }

        @include hover() {
            background: var(--color-theme-primary);

            .ticket-action,
            .ticket-navigation {
                background: var(--color-darth-maul);

                @include hover() {
                    background: var(--color-red-raspberry);
                    fill: #fff;
                }
            }
        }
    }

    @include mobile() {
        height: auto;
        display: flex;
        flex-wrap: wrap;
        padding: 2rem;

        .ticket-timestamp {
            width: calc(100% - 4.4rem);
        }

        .ticket-meta {
            &-kayako, &-content {
                width: calc(50% - 1rem);
            }
            &-content .truncated-text {
                -webkit-line-clamp: unset;
            }
            &-client {
                width: 100%;
                padding: 2rem 0;
                border: solid var(--color-mist);
                border-width: .1rem 0;
            }
            &-status, &-assignee {
                width: 100%;
            }
        }

        .ticket-action {
            margin-left: auto;
        }

        .ticket-assign {
            width: calc(100% - 11.4rem);
        }
    }
}
</style>
